# Rolfs single hotel
# Once the endpoint is fixed, use InfinityScrollTripTable here instead of pagination
<template>
  <NuxtLayout
    v-if="hotel"
    name="banner-default"
  >
    <div class="md:w-12/12 product-content__hotel--wrap px-0 pb-12 pt-6 md:pt-8">
      <BedplaceHotel
        class="mb-12"
        :hotel="hotel"
        is-hotel-page
      />
    </div>
  </NuxtLayout>
</template>

<script setup>
definePageMeta({
  transition: 'pageonly',
})

const { $sentryCaptureMessage } = useNuxtApp()
const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const { urls: localeURLs } = useLocale()
const localeStore = useLocaleStore()
const { imageSize } = useHotelImagesMixin()
const { setPageBanner } = useBanner()

const { error, data } = await useAsyncData(async () => {
  try {
    const [{ data: hotel }] = await Promise.all([
      apiFetch(`/${localeStore.locale}/hotel/${route.params.name}`),
    ])

    const hotelGiataId = getHotelGiataId(hotel)
    let hotelDescriptionAI
    if (hotelGiataId) {
      hotelDescriptionAI = (await apiFetch(`/${localeStore.locale}/hoteldescription/${hotelGiataId}`))?.data
    }
    const processsedHotel = processHotelInfo(hotel, { hotelDescriptionAI })

    setPageBanner({
      background: { image: processsedHotel?.hotelImage },
    })

    return {
      hotel: processsedHotel,
    }
  } catch (e) {
    $sentryCaptureMessage('hotels/getHotelInfo error', {
      level: 'error',
      contexts: {
        details: {
          e,
        },
      },
      tags: {
        type: 'UX',
      },
    })
    throw new Error('Post not found: ' + router.currentRoute.value.fullPath)
  }
})

const { $isSol } = useNuxtApp()

if (error.value) {
  const url = $isSol ? localeStore.urls?.hotel : localeStore.urls?.hotels
  navigateTo(url)
}

const { hotel } = data.value

const getImagePath = () => {
  const image = hotel?.images?.length > 0 && hotel?.images[0].image
  if (!image) {
    return
  }

  if (Array.isArray(image)) {
    return (image.find(i => i?.maxwidth >= 1200) || image[image.length - 1]).href
  }

  return formatCloudinaryImagePath(
    imageSize(image, 'large'),
    {
      transformation: { watermark: true },
      forceJPG: true,
      isSol: $isSol,
    }
  )
}

const currentSlug = route.params.name
const correctSlug = hotel?.urlName || hotel?.url_name || currentSlug
const fullSlug = `${localeURLs.hotels}/${correctSlug}`

onMounted(() => {
  if (currentSlug.toLowerCase() !== correctSlug.toLowerCase()) {
    const message = {
      'Urls': {
        current_url: currentSlug,
        url_from_api: correctSlug,
      },
    }
    $sentryCaptureMessage('hotels/url mismatch', {
      level: 'info',
      contexts: message,
      tags: { type: 'UX' },
    })
  }
})

useHead({
  title: () => hotel.propertyName,
  link: [{ rel: 'canonical', href: () => `${localeURLs.BASE_URL}${fullSlug}` }],
  meta: [
    { hid: 'author', name: 'author', content: () => t('siteTitleBrand') },
    {
      hid: 'og:url',
      property: 'og:url',
      content: () => `${localeURLs.BASE_URL}${fullSlug}`,
    },
    {
      hid: 'og:title',
      property: 'og:title',
      content: () => `${hotel.propertyName} - ${t('siteTitleBrand')}`,
    },
    {
      hid: 'og:image',
      property: 'og:image',
      content: () => getImagePath(),
    },
    {
      hid: 'og:description',
      name: 'og:description',
      content: () => hotel?.description || hotel?.info,
    },
    {
      hid: 'description',
      name: 'description',
      content: () => hotel?.description || hotel?.info,
    },
  ],
})
</script>

<style lang="scss">
@import "@layers/web/assets/scss/modules/product/_product-hotel-list";
@import "@layers/web/assets/scss/modules/product/_product-testimonials";
</style>
